import React, { createContext, useContext, useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { useCookies } from 'react-cookie';
import { createNotificationApi } from '../../controller/api/customerNotifications';
import { findNotificationRecipients } from '../../controller/api/notificationRecipients';
import { mutationRunner } from './model';

const Context = createContext(null);
export function NotificationProvider({ children }) {
  const [cookies] = useCookies(['lottmekong']);
  const value = useMemo(() => {
    const api = createNotificationApi(cookies.lottmekong);
    return { api, mutate: mutationRunner(api), findRecipients: phone => findNotificationRecipients(cookies.lottmekong, phone) };
  }, [cookies.lottmekong]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
export const useNotificationApi = () => useContext(Context);

export function useQuery(path, query = {}) {
  const { api } = useNotificationApi();
  const queryKey = JSON.stringify(query);
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState({ data: null, error: null, loading: !!path });
  useEffect(() => {
    let live = true;
    if (!path) { setResult({ data: null, error: null, loading: false }); return () => { live = false; }; }
    setResult({ data: null, error: null, loading: true });
    api('GET', path, { query: JSON.parse(queryKey) }).then(response => {
      if (live) setResult({ ...response, error: null, loading: false });
    }).catch(error => { if (live) setResult({ data: null, error, loading: false }); });
    return () => { live = false; };
  }, [api, path, queryKey, revision]);
  const refresh = useCallback(() => setRevision(value => value + 1), []);
  return { ...result, refresh };
}
export function useMutation() {
  const { mutate } = useNotificationApi();
  const [state, setState] = useState({ busy: false, error: null, success: '' });
  const active = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true; active.current = false;
    setState({ busy: false, error: null, success: '' });
    return () => { mounted.current = false; active.current = false; };
  }, [mutate]);
  const run = async (method, path, options, message = '') => {
    if (active.current) return null;
    const operation = {};
    active.current = operation; setState({ busy: true, error: null, success: '' });
    try {
      const result = await mutate(method, path, options);
      if (!mounted.current || active.current !== operation) return null;
      setState({ busy: false, error: null, success: message });
      return result;
    } catch (error) {
      if (mounted.current && active.current === operation) setState({ busy: false, error, success: '' });
      return null;
    } finally { if (active.current === operation) active.current = false; }
  };
  const clear = useCallback(() => setState({ busy: false, error: null, success: '' }), []);
  return { ...state, run, clear };
}
